import { LoadingOutlined } from '@ant-design/icons';
import { type ChangeRequest } from 'models';
import { getAppConfig } from 'old/atoms/app-config-gate';
import { Trans, useTranslation } from 'react-i18next';
import variables from 'common/styles/variables.json';
import { isPasswordValid } from 'common/validators';
import { CheckboxInput, Col, Form, FormButton, PasswordInput, Row, Skeleton, TextInput, Text, Link } from 'ui';
import { useChangeRequestsQuery } from '../queries/useChangeRequestsQuery';

type P = {
  onSubmit: (data: FormValues) => void;
  loading: boolean;
  changeRequestId?: string;
  invitationToken?: string;
};

const getInitialValues = (changeRequestData?: ChangeRequest): Partial<FormValues> => ({
  firstName: changeRequestData?.meta.firstName,
  lastName: changeRequestData?.meta.lastName,
  email: changeRequestData?.meta.invitationEmail,
});

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const SignUpForm = ({ onSubmit, loading, changeRequestId, invitationToken }: P) => {
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation();
  const { screens } = getAppConfig();

  const {
    data: changeRequestData,
    isLoading,
    isFetching,
  } = useChangeRequestsQuery({
    changeRequestId,
    invitationToken,
  });

  const initialValues = getInitialValues(changeRequestData);

  const onFinish = (data: FormValues) => onSubmit(data);

  return (
    <div className="sign-up-form">
      {isLoading && isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} className="sign-up-form__loading" />
      ) : (
        <Form<FormValues> onFinish={onFinish} form={form} initialValues={initialValues}>
          <Row item={{ gutter: variables.spaceMd.value }}>
            <Col item={{ span: 12 }}>
              <TextInput
                item={{
                  name: 'firstName',
                  label: 'First name',
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                      message: t('This field is required'),
                    },
                  ],
                }}
              />
            </Col>
            <Col item={{ span: 12 }}>
              <TextInput
                item={{
                  name: 'lastName',
                  label: 'Last name',
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                      message: t('This field is required'),
                    },
                  ],
                }}
              />
            </Col>
          </Row>
          <TextInput
            item={{
              name: 'email',
              label: 'Email',
              rules: [
                {
                  type: 'email',
                  message: t('E-mail is not valid'),
                },
                {
                  required: true,
                  whitespace: true,
                  message: t('This field is required'),
                },
              ],
            }}
          />
          <PasswordInput
            item={{
              name: 'password',
              label: 'Password',
              rules: [
                {
                  required: true,
                  whitespace: true,
                  message: t('This field is required'),
                },
                () => ({
                  message: '',
                  validator: async (_, value) => (isPasswordValid(value) ? Promise.resolve() : Promise.reject()),
                }),
              ],
            }}
            showValidation
          />
          <CheckboxInput
            item={{
              label: (
                <Text item={{ type: 'secondary' }}>
                  <Trans>
                    I agree with
                    <Link item={{ to: screens.terms_screen.link, target: '_blank', rel: 'noopener noreferrer' }}>
                      Terms
                    </Link>
                    and
                    <Link item={{ to: screens.privacy_screen.link, target: '_blank', rel: 'noopener noreferrer' }}>
                      Privacy Policy
                    </Link>
                  </Trans>
                </Text>
              ),
              name: 'termsAgreement',
              valuePropName: 'checked',
              rules: [
                {
                  message: t('This field must be accepted'),
                  validator: async (_, value) => (value ? Promise.resolve() : Promise.reject()),
                },
              ],
            }}
          />
          <FormButton
            button={{
              id: 'create-account',
              title: 'Sign up',
              className: 'sign-up-form__submit',
              loading,
              icon: loading && <LoadingOutlined />,
            }}
          />
        </Form>
      )}
    </div>
  );
};

export default SignUpForm;
